import type { ReactNode } from 'react';
import { Button, Select, SelectContent, SelectItem, SelectTrigger } from '@design-system';
import { missingDetailsCount, type ConsumerApplicationSection } from '@shared/consumerPlan';
import { useApplication } from '../hooks/use-application';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { APPLICATION_SECTION_STEPS } from '../lib/steps';
import { SectionStatusBadge } from './section-status-badge';
import { StepFrame } from './step-frame';

export const APPLICATION_SECTION_TITLES: Record<ConsumerApplicationSection, string> = {
  personal: 'Personal information',
  medical: 'Medical information',
  health: 'Health information',
  beneficiaries: 'Potential beneficiaries',
};

interface ApplicationSectionFrameProps {
  clientId: string;
  section: ConsumerApplicationSection;
  title: string;
  subtitle: string;
  // The section's edited answers, saved when the consumer moves on
  draft: Partial<ConsumerApplicationAnswers>;
  children: ReactNode;
}

function ApplicationSectionOption({
  position,
  section,
  missing,
}: {
  position: number;
  section: ConsumerApplicationSection;
  missing: number;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2 text-left">
      <span>
        {position + 1}. {APPLICATION_SECTION_TITLES[section]}
      </span>
      <SectionStatusBadge missing={missing} />
    </span>
  );
}

/** Phones get one section menu; wider screens keep every section visible as a chip. */
function ApplicationSectionSwitcher({
  section,
  sections,
  answers,
  onSelect,
}: {
  section: ConsumerApplicationSection;
  sections: ConsumerApplicationSection[];
  answers: ConsumerApplicationAnswers;
  onSelect: (section: ConsumerApplicationSection) => void;
}) {
  const currentIndex = sections.indexOf(section);

  return (
    <>
      <Select value={section} onValueChange={value => onSelect(value as ConsumerApplicationSection)}>
        <SelectTrigger
          aria-label="Application sections"
          className="h-auto w-full py-2 whitespace-normal data-[size=default]:h-auto sm:hidden"
        >
          <ApplicationSectionOption
            position={currentIndex}
            section={section}
            missing={missingDetailsCount(answers, section)}
          />
        </SelectTrigger>
        <SelectContent align="start" className="sm:hidden">
          {sections.map((entry, position) => (
            <SelectItem key={entry} value={entry} className="py-2">
              <ApplicationSectionOption
                position={position}
                section={entry}
                missing={missingDetailsCount(answers, entry)}
              />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <nav aria-label="Application sections" className="hidden flex-wrap gap-2 sm:flex">
        {sections.map((entry, position) => {
          const missing = missingDetailsCount(answers, entry);
          return (
            <Button
              key={entry}
              variant="outline"
              size="sm"
              aria-current={entry === section ? 'step' : undefined}
              className={entry === section ? 'border-primary hover:border-primary' : undefined}
              onClick={() => onSelect(entry)}
            >
              {position + 1}. {APPLICATION_SECTION_TITLES[entry]}
              <SectionStatusBadge missing={missing} />
            </Button>
          );
        })}
      </nav>
    </>
  );
}

/** One application section with the section navigator and save-and-continue. */
export function ApplicationSectionFrame({
  clientId,
  section,
  title,
  subtitle,
  draft,
  children,
}: ApplicationSectionFrameProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { answers, sections, saveAnswers } = useApplication(clientId);
  const index = sections.indexOf(section);
  const nextSection = sections[index + 1];
  const previousSection = sections[index - 1];
  const merged = { ...answers, ...draft };

  const saveAndGo = (target: ConsumerApplicationSection | 'review') => {
    saveAnswers(draft);
    goTo(target === 'review' ? 'application-review' : APPLICATION_SECTION_STEPS[target]);
  };

  return (
    <StepFrame
      step={APPLICATION_SECTION_STEPS[section]}
      title={title}
      subtitle={subtitle}
      onBack={() => back(previousSection ? APPLICATION_SECTION_STEPS[previousSection] : 'application-start')}
      help={
        <>
          <p>Answer what you can. It’s okay to leave gaps; our team will follow up before anything goes to the insurance company.</p>
          <p>Your answers are saved as you move between sections.</p>
        </>
      }
      primary={{ label: 'Save and continue', onClick: () => saveAndGo(nextSection ?? 'review') }}
      next={nextSection ? APPLICATION_SECTION_TITLES[nextSection].toLowerCase() + '.' : 'review your application.'}
    >
      <ApplicationSectionSwitcher section={section} sections={sections} answers={merged} onSelect={saveAndGo} />
      <div className="flex flex-col gap-4">{children}</div>
    </StepFrame>
  );
}
